import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FONT_SIZES, FONT_WEIGHTS, RADIUS_PILL } from '../../../../Theme';
import {
  AuthenticatedLandingBandRules,
  FINAL_CTA_BAR_PADDING_Y,
  FINAL_CTA_FRAME_PADDING_X,
  FINAL_CTA_FRAME_PADDING_X_WIDE,
  MarketingSection,
  authenticatedCategoryAlignedContainerSx,
  marketingCategoryAlignedContainerSx,
} from '../../styles/marketingLandingStyles';
import { MARKETING_FINAL_CTA_LOGGED_OUT } from '../../data/marketingLandingData';

function solidPillSx() {
  return {
    borderColor: 'neutral.main',
    backgroundColor: 'neutral.main',
    color: 'neutral.contrastText',
    '&:hover': {
      backgroundColor: 'text.secondary',
      borderColor: 'text.secondary',
    },
  };
}

function outlinePillSx() {
  return {
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'background.softHover',
      borderColor: 'divider',
    },
  };
}

function CtaPill({ to, children, variant = 'outline' }) {
  const isSolid = variant === 'solid';

  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        borderRadius: `${RADIUS_PILL}px`,
        fontWeight: FONT_WEIGHTS.medium,
        fontSize: FONT_SIZES.bodySm,
        lineHeight: 1,
        textDecoration: 'none',
        border: '1px solid',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        ...(isSolid ? solidPillSx() : outlinePillSx()),
      }}
    >
      {children}
    </Box>
  );
}

function framedCtaContainerSx() {
  return {
    ...marketingCategoryAlignedContainerSx,
    px: {
      xs: `${FINAL_CTA_FRAME_PADDING_X}px`,
      sm: `${FINAL_CTA_FRAME_PADDING_X_WIDE}px`,
    },
  };
}

function MarketingFinalCTA({ brandLanding = false, framePadding = false }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userType = useSelector((state) => state.auth.userType);
  const isBrand = isAuthenticated && userType === 'brand';
  const isInfluencer = isAuthenticated && userType === 'influencer';
  const loggedOutCopy = MARKETING_FINAL_CTA_LOGGED_OUT;
  const showDashboardCta = isBrand || isInfluencer;

  let { heading } = loggedOutCopy;
  if (isBrand) heading = 'Keep collaborating with live sellers on Millet';
  else if (isInfluencer) heading = 'Keep growing on Millet';

  let outlineLabel = loggedOutCopy.primaryLabel;
  let outlineTo = loggedOutCopy.primaryTo;
  let solidLabel = loggedOutCopy.secondaryLabel;
  let solidTo = loggedOutCopy.secondaryTo;
  if (showDashboardCta) {
    outlineLabel = 'Browse creators';
    outlineTo = '/marketplace/influencers';
    solidLabel = 'Go to dashboard';
    solidTo = '/dashboard/overview';
    if (isInfluencer) {
      outlineLabel = 'Browse campaigns';
      outlineTo = '/marketplace/campaigns';
    }
  }

  const ctaRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: '16px',
      }}
    >
      <Typography
        id="marketing-final-cta-heading"
        component="h2"
        sx={{
          margin: 0,
          fontWeight: FONT_WEIGHTS.medium,
          fontSize: FONT_SIZES.title,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          color: 'text.primary',
        }}
      >
        {heading}
      </Typography>
      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flexShrink: 0 }}>
        <CtaPill to={outlineTo} variant="outline">
          {outlineLabel}
        </CtaPill>
        <CtaPill to={solidTo} variant="solid">
          {solidLabel}
        </CtaPill>
      </Box>
    </Box>
  );

  if (brandLanding) {
    return (
      <MarketingSection
        component="section"
        aria-labelledby="marketing-final-cta-heading"
        sx={{
          position: 'relative',
          flexGrow: 1,
          paddingTop: `${FINAL_CTA_BAR_PADDING_Y}px`,
          paddingBottom: `${FINAL_CTA_BAR_PADDING_Y}px`,
        }}
      >
        <AuthenticatedLandingBandRules />
        <Box
          sx={{
            ...authenticatedCategoryAlignedContainerSx,
            px: {
              xs: `${FINAL_CTA_FRAME_PADDING_X}px`,
              sm: authenticatedCategoryAlignedContainerSx.px,
            },
          }}
        >
          {ctaRow}
        </Box>
      </MarketingSection>
    );
  }

  return (
    <MarketingSection
      component="section"
      aria-labelledby="marketing-final-cta-heading"
    >
      <Box sx={framePadding ? framedCtaContainerSx() : marketingCategoryAlignedContainerSx}>
        {ctaRow}
      </Box>
    </MarketingSection>
  );
}

export default MarketingFinalCTA;
